import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { confirmDialog } from 'primereact/confirmdialog';
import { Toast } from 'primereact/toast';
import { OverlayPanel } from 'primereact/overlaypanel';

import { MarsinetContext } from '../../../../../context/marsinetProvider';
import { TableSurveysComponentProps } from '../../../../../models/surveysModels';
import { ProgressBarComponent } from '../../../../../components/progress-bar/progress-bar';
import { configureAxios } from '../../../../../utils/utils';
import { apiServices } from '../../../../../services/apiServices';
import { analytics } from '../../../../../../firebase';
import { logEvent } from 'firebase/analytics';
import { SurveyTableDto } from 'apps/marsinet/src/app/typescript-axios';
import UserActionOverlay from '../../../users/components/overlay-panel-user/userActionOverlay';
import RecipientsDialog from '../recipients-dialog/recipients-dialog';
import { formatUtcTimestamp } from '../../../../../utils/timezone-utils';

const TableSurveysComponent: React.FC<TableSurveysComponentProps> = ({
  filtersSurveys,
  goToSurvey,
  loading,
  onSurveyDeleted,
  onSurveyStatusChanged,
}) => {
  const { t, i18n } = useTranslation();
  const { access_token, setRole, setAdmin, setError, selectedTimezone } =
    useContext(MarsinetContext);

  const dt = useRef<DataTable<SurveyTableDto[]>>(null);
  const toast = useRef<Toast>(null);
  const overlayRef = useRef<OverlayPanel>(null);
  const [selectedSurveyForRecipients, setSelectedSurveyForRecipients] =
    useState<SurveyTableDto | null>(null);
  const [showRecipientsDialog, setShowRecipientsDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState<'add' | 'delete'>('add');
  const [availableOrganizations, setAvailableOrganizations] = useState<any[]>(
    []
  );
  const [selectedOrganizations, setSelectedOrganizations] = useState<string[]>(
    []
  );
  const [currentActions, setCurrentActions] = useState<
    Array<{
      label: string;
      icon: string;
      className: string;
      command: () => void;
    }>
  >([]);

  useEffect(() => {
    const fetchAccountData = async () => {
      if (!access_token) return;
      configureAxios(access_token);

      try {
        const response =
          await apiServices.myAccountApi.myAccountControllerGetData();
        const data = response.data;
        setRole(data?.organization?.role);
        setAdmin(data?.can_edit);
      } catch (error) {
        if (error.response?.status !== 401) {
          setError(true);
          logEvent(analytics, 'error_marsinet', {
            error_message: error.message,
            error_code: error.response?.status,
          });
        }
      }
    };

    fetchAccountData();
  }, [access_token, setAdmin, setRole, setError]);

  const handleDelete = async (rowData: SurveyTableDto) => {
    if (!access_token) return;
    configureAxios(access_token);

    try {
      await apiServices.surveyApi.surveyControllerDeleteSurvey(rowData.id);
      onSurveyDeleted(rowData.id);
      toast.current?.show({
        severity: 'success',
        summary: t('pages.surveys.delete.success_summary'),
        detail: t('pages.surveys.delete.success_detail', {
          name: rowData.name,
        }),
        life: 3000,
      });
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: t('pages.surveys.delete.error_summary'),
        detail: t('pages.surveys.delete.error_detail'),
        life: 3000,
      });
      logEvent(analytics, 'error_marsinet', {
        error_message: error.message,
        error_code: error.response?.status,
      });
    }
  };

  const handleDownloadCsv = async (surveyId: string, surveyName: string) => {
    if (!access_token) return;
    configureAxios(access_token);

    try {
      const response =
        (await apiServices.surveyApi.surveyControllerGetSurveyAnswersCsv(
          surveyId,
          { responseType: 'blob' }
        )) as any;

      if (response?.data) {
        const blob = new Blob([response.data], {
          type: 'text/csv;charset=utf-8;',
        });

        // Crear URL y descargar
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute(
          'download',
          `survey_${surveyName}_${new Date().toISOString().split('T')[0]}.csv`
        );
        link.style.visibility = 'hidden';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Liberar memoria
        URL.revokeObjectURL(url);

        toast.current?.show({
          severity: 'success',
          summary: t('messages.success'),
          detail: 'CSV descargado correctamente',
          life: 3000,
        });
      }
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: t('messages.error'),
        detail: 'Error al descargar el CSV',
        life: 3000,
      });
      logEvent(analytics, 'error_marsinet', {
        error_message: error.message,
        error_code: error.response?.status,
      });
    }
  };

  const handlePause = async (surveyId: string) => {
    if (!access_token) return;
    configureAxios(access_token);

    try {
      await apiServices.surveyApi.surveyControllerDeactivateSurvey(surveyId);
      onSurveyStatusChanged?.(surveyId, false);
      toast.current?.show({
        severity: 'success',
        summary: t('messages.success'),
        detail: t('messages.surveys.survey_paused_success'),
        life: 3000,
      });
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: t('messages.error'),
        detail: t('messages.surveys.survey_paused_error'),
        life: 3000,
      });
      logEvent(analytics, 'error_marsinet', {
        error_message: error.message,
        error_code: error.response?.status,
      });
    }
  };

  const handleResume = async (surveyId: string) => {
    if (!access_token) return;
    configureAxios(access_token);

    try {
      await apiServices.surveyApi.surveyControllerActivateSurvey(surveyId);
      onSurveyStatusChanged?.(surveyId, true);
      toast.current?.show({
        severity: 'success',
        summary: t('messages.success'),
        detail: t('messages.surveys.survey_resumed_success'),
        life: 3000,
      });
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: t('messages.error'),
        detail: t('messages.surveys.survey_resumed_error'),
        life: 3000,
      });
      logEvent(analytics, 'error_marsinet', {
        error_message: error.message,
        error_code: error.response?.status,
      });
    }
  };

  const handleAddRecipients = async (surveyId: string) => {
    if (!access_token || selectedOrganizations.length === 0) return;
    configureAxios(access_token);

    try {
      await apiServices.surveyApi.surveyControllerSubscribeOrgsToSurvey(
        { organization_ids: selectedOrganizations },
        surveyId
      );
      // Actualizar el estado para mostrar orgs asignadas
      if (selectedSurveyForRecipients) {
        const updatedOrganizationIds = [
          ...(selectedSurveyForRecipients.organization_ids || []),
          ...selectedOrganizations,
        ];
        setSelectedSurveyForRecipients({
          ...selectedSurveyForRecipients,
          organization_ids: updatedOrganizationIds,
        });
      }
      // borrar las orgs agregadas de la lista disponible
      setAvailableOrganizations((prev) =>
        prev.filter((org) => !selectedOrganizations.includes(org.o_id))
      );
      setShowRecipientsDialog(false);
      setSelectedOrganizations([]);
      toast.current?.show({
        severity: 'success',
        summary: t('messages.success'),
        detail: t('messages.surveys.recipients_added_success'),
        life: 3000,
      });
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: t('messages.error'),
        detail: t('messages.surveys.recipients_added_error'),
        life: 3000,
      });
      logEvent(analytics, 'error_marsinet', {
        error_message: error.message,
        error_code: error.response?.status,
      });
    }
  };

  const handleDeleteRecipients = async (surveyId: string) => {
    if (!access_token || selectedOrganizations.length === 0) return;
    configureAxios(access_token);

    try {
      await apiServices.surveyApi.surveyControllerUnsubscribeOrgsFromSurvey(
        { organization_ids: selectedOrganizations },
        surveyId
      );

      if (selectedSurveyForRecipients) {
        const updatedOrganizationIds =
          selectedSurveyForRecipients.organization_ids?.filter(
            (id) => !selectedOrganizations.includes(id)
          ) || [];
        setSelectedSurveyForRecipients({
          ...selectedSurveyForRecipients,
          organization_ids: updatedOrganizationIds,
        });
      }

      const deletedOrgs = selectedOrganizations
        .map((orgId) =>
          availableOrganizations.find((org) => org.o_id === orgId)
        )
        .filter(Boolean);

      setAvailableOrganizations((prev) => [...prev, ...deletedOrgs]);

      setShowRecipientsDialog(false);
      setSelectedOrganizations([]);
      toast.current?.show({
        severity: 'success',
        summary: t('messages.success'),
        detail: t('messages.surveys.recipients_deleted_success'),
        life: 3000,
      });
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: t('messages.error'),
        detail: t('messages.surveys.recipients_deleted_error'),
        life: 3000,
      });
      logEvent(analytics, 'error_marsinet', {
        error_message: error.message,
        error_code: error.response?.status,
      });
    }
  };

  const openRecipientsDialog = async (
    survey: SurveyTableDto,
    mode: 'add' | 'delete'
  ) => {
    const surveyToUse =
      selectedSurveyForRecipients?.id === survey.id
        ? selectedSurveyForRecipients
        : survey;

    setSelectedSurveyForRecipients(surveyToUse);
    setSelectedOrganizations([]);
    setDialogMode(mode);

    if (!access_token) return;
    configureAxios(access_token);

    try {
      const response =
        await apiServices.userApi.usersControllerGetOrganizations();

      let filteredOrganizations;
      if (mode === 'add') {
        // Para agregar: mostrar organizaciones no asignadas
        filteredOrganizations = (response.data || []).filter(
          (org) => !surveyToUse.organization_ids?.includes(org.o_id)
        );
      } else {
        // Para eliminar: mostrar organizaciones asignadas
        filteredOrganizations = (response.data || []).filter((org) =>
          surveyToUse.organization_ids?.includes(org.o_id)
        );
      }
      setAvailableOrganizations(filteredOrganizations);
    } catch (error) {
      console.error('Error loading organizations', error);
    }

    setShowRecipientsDialog(true);
  };

  const getActionsForRow = (row: SurveyTableDto) => [
    {
      label: row.is_active
        ? t('pages.surveys.pausar')
        : t('pages.surveys.reanudar'),
      icon: row.is_active ? 'pi pi-pause' : 'pi pi-play',
      className: 'p-button-text',
      command: () => {
        row.is_active ? handlePause(row.id) : handleResume(row.id);
        overlayRef.current?.hide();
      },
    },
    {
      label: t('pages.surveys.add_recipients'),
      icon: 'pi pi-plus',
      className: 'p-button-text',
      command: () => {
        openRecipientsDialog(row, 'add');
        overlayRef.current?.hide();
      },
    },
    {
      label: t('pages.surveys.delete_recipients'),
      icon: 'pi pi-minus',
      className: 'p-button-text',
      command: () => {
        openRecipientsDialog(row, 'delete');
        overlayRef.current?.hide();
      },
    },
    {
      label: t('pages.surveys.descargarrespuestasencvs'),
      icon: 'pi pi-download',
      className: 'p-button-text',
      command: () => {
        handleDownloadCsv(row.id, row.name);
        overlayRef.current?.hide();
      },
    },
    {
      label: t('pages.surveys.borrar'),
      icon: 'pi pi-trash',
      className: 'p-button-danger p-button-text',
      command: () => {
        overlayRef.current?.hide();
        confirmDialog({
          message: t('pages.surveys.delete.confirm_message', {
            name: row.name,
          }),
          header: t('pages.surveys.delete.confirm_header'),
          icon: 'pi pi-exclamation-triangle',
          acceptClassName: 'p-button-danger',
          acceptLabel: t('pages.surveys.delete.confirm_accept'),
          rejectLabel: t('pages.surveys.delete.confirm_reject'),
          accept: () => handleDelete(row),
        });
      },
    },
  ];

  const handleMenuClick = (event: React.MouseEvent, row: SurveyTableDto) => {
    event.stopPropagation();
    setCurrentActions(getActionsForRow(row));
    overlayRef.current?.show(event, event.currentTarget);
  };

  const nameBodyTemplate = (rowData: SurveyTableDto) => (
    <div className="pl-3">{rowData.name}</div>
  );

  const createdAtBodyTemplate = (rowData: SurveyTableDto) => (
    <div className="pl-3">
      {/* Las fechas de creación llegan en UTC y se muestran en la zona activa. */}
      {formatUtcTimestamp(
        rowData.created_at,
        selectedTimezone,
        i18n.language,
        { dateStyle: 'medium' }
      )}
    </div>
  );

  const activeBodyTemplate = (rowData: SurveyTableDto) => (
    <div className="pl-3">
      {rowData.is_active ? (
        <span className="p-tag p-tag-success">{t('status.active')}</span>
      ) : (
        <span className="p-tag p-tag-warning">{t('status.paused')}</span>
      )}
    </div>
  );

  const renderHeader = () => (
    <div className="flex flex-col sm:flex-row justify-between gap-3">
      <div className="w-full sm:w-auto flex justify-center sm:justify-start">
        <Button
          type="button"
          icon="pi pi-filter-slash"
          label="Clear"
          outlined
          className="w-full sm:w-auto text-xs sm:text-sm px-3 py-2 lg:px-4 lg:py-2"
        />
      </div>
      <div className="p-inputgroup"></div>
    </div>
  );

  return (
    <div>
      <Toast ref={toast} />
      {loading ? (
        <ProgressBarComponent />
      ) : (
        <DataTable
          value={filtersSurveys}
          paginator
          ref={dt}
          sortOrder={1}
          rows={10}
          header={renderHeader()}
          rowsPerPageOptions={[5, 10, 25, 50]}
          size="normal"
          loading={loading}
          removableSort
          emptyMessage={t('emptyValuesTable')}
        >
          <Column
            header={t('pages.surveys.table.created_by')}
            field="name"
            sortField="name"
            filter
            showAddButton={false}
            showFilterMatchModes={false}
            showApplyButton={true}
            showFilterMenuOptions={false}
            filterPlaceholder={t('pages.surveys.table.filters.created_by')}
            style={{ width: '10rem' }}
            body={(rowData) => (
              <div className="pl-3">
                {rowData.created_by ? rowData.created_by : t('noData')}
              </div>
            )}
            headerClassName="pl-5"
          />
          <Column
            header={t('pages.surveys.table.name')}
            field="name"
            style={{ width: '8rem' }}
            body={nameBodyTemplate}
            headerClassName="pl-3"
            sortable
          />
          <Column
            header={t('pages.surveys.table.created_at')}
            field="created_at"
            style={{ width: '8rem' }}
            body={createdAtBodyTemplate}
            sortable
          />
          <Column
            header={t('pages.surveys.table.active')}
            field="is_active"
            style={{ width: '5rem' }}
            body={activeBodyTemplate}
            sortable
          />
          <Column
            header={t('pages.surveys.table.details')}
            style={{ width: '1rem' }}
            body={(row: SurveyTableDto) => (
              <div className="pl-1">
                <Button
                  className="p-button-rounded p-button-text p-button-lg p-button-secondary"
                  icon="pi pi-eye"
                  severity="secondary"
                  tooltip={t('view_details')}
                  onClick={() => goToSurvey(row)}
                  tooltipOptions={{
                    position: 'bottom',
                    mouseTrack: true,
                    mouseTrackTop: 15,
                  }}
                />
              </div>
            )}
          />
          <Column
            header={t('pages.devices.table.actions')}
            style={{ width: '2rem' }}
            body={(row: SurveyTableDto) => (
              <div className="pl-1">
                <Button
                  className="p-button-rounded p-button-text p-button-lg p-button-secondary"
                  icon="pi pi-ellipsis-v"
                  severity="secondary"
                  tooltip={t('pages.devices.table.actions')}
                  onClick={(e) => handleMenuClick(e, row)}
                  tooltipOptions={{
                    position: 'bottom',
                    mouseTrack: true,
                    mouseTrackTop: 15,
                  }}
                />
              </div>
            )}
          />
        </DataTable>
      )}
      <UserActionOverlay actions={currentActions} overlayRef={overlayRef} />
      <RecipientsDialog
        visible={showRecipientsDialog}
        onHide={() => setShowRecipientsDialog(false)}
        organizations={availableOrganizations}
        selectedOrganizations={selectedOrganizations}
        onSelectionChange={setSelectedOrganizations}
        onConfirm={() => {
          if (selectedSurveyForRecipients) {
            if (dialogMode === 'add') {
              handleAddRecipients(selectedSurveyForRecipients.id);
            } else {
              handleDeleteRecipients(selectedSurveyForRecipients.id);
            }
          }
        }}
        dialogType={dialogMode}
      />
    </div>
  );
};

export default TableSurveysComponent;
